import React from 'react';
import { LEGAL_PRIVACY_MD, LEGAL_TERMS_MD } from '../constants/legalDocuments';
import { fetchSignupLegalDocuments } from '../services/signupLegalService';
import { sanitizeSchoolNoticeHtml } from '../utils/schoolNoticeHtml';
import './LegalDocumentPage.css';

export type LegalDocKind = 'privacy' | 'terms';

export const LEGAL_DOC_PATHS: Record<LegalDocKind, string> = {
  privacy: '/privacy',
  terms: '/terms',
};

/** 환불 기준 링크(이용약관 제4장 이용권 결제 및 환불) */
export const LEGAL_REFUND_HREF = '/terms#refund';

const DOCS: Record<LegalDocKind, { label: string; md: string }> = {
  terms: { label: '이용약관', md: LEGAL_TERMS_MD },
  privacy: { label: '개인정보처리방침', md: LEGAL_PRIVACY_MD },
};

/** 현재 URL이 공개 법적 고지 페이지면 종류 반환 (/privacy/neis-extension 은 제외 — 정적 html) */
export function getLegalDocKindFromPath(): LegalDocKind | null {
  if (typeof window === 'undefined') return null;
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let path = window.location.pathname;
  if (base && path.startsWith(base)) path = path.slice(base.length) || '/';
  path = path.replace(/\/+$/, '') || '/';
  if (path === '/privacy') return 'privacy';
  if (path === '/terms') return 'terms';
  return null;
}

function renderInline(text: string, keyBase: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**') && p.length > 4) {
      return <strong key={`${keyBase}-${i}`}>{p.slice(2, -2)}</strong>;
    }
    const segs = p.split(/(\[미정\]|\[확인 필요[^\]]*\])/g);
    return segs.map((s, j) =>
      /^\[(미정|확인 필요)/.test(s) ? (
        <mark key={`${keyBase}-${i}-${j}`} className="cp-legal-pending">
          {s}
        </mark>
      ) : (
        <React.Fragment key={`${keyBase}-${i}-${j}`}>{s}</React.Fragment>
      )
    );
  });
}

function splitRow(line: string): string[] {
  return line.replace(/^\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim());
}

/** 간단 마크다운(제목·목록·표·굵게·문단) 렌더 */
function renderMarkdown(md: string): React.ReactNode[] {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: React.ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const bk = k;
    const line = lines[i];
    const t = line.trim();
    if (!t) {
      i += 1;
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(t);
    if (h) {
      const level = h[1].length;
      const Tag = (`h${Math.min(level + 1, 5)}`) as 'h2' | 'h3' | 'h4' | 'h5';
      const id = /^제4장/.test(h[2]) ? 'refund' : /^\[별표\]/.test(h[2]) ? 'refund-detail' : undefined;
      out.push(
        <Tag key={k++} id={id}>
          {renderInline(h[2], `h${bk}`)}
        </Tag>
      );
      i += 1;
      continue;
    }
    if (t.startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const r = lines[i].trim();
        if (!/^\|[\s:|-]+\|?$/.test(r)) rows.push(splitRow(r));
        i += 1;
      }
      const [head, ...body] = rows;
      out.push(
        <div className="cp-legal-table-wrap" key={k++}>
          <table className="cp-legal-table">
            <thead>
              <tr>{head.map((c, ci) => <th key={ci}>{renderInline(c, `th${bk}-${ci}`)}</th>)}</tr>
            </thead>
            <tbody>
              {body.map((r, ri) => (
                <tr key={ri}>{r.map((c, ci) => <td key={ci}>{renderInline(c, `td${bk}-${ri}-${ci}`)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    if (/^([-*]|\d+\.)\s+/.test(t)) {
      const ordered = /^\d+\./.test(t);
      const items: { text: string; subs: string[] }[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
        const raw = lines[i];
        const text = raw.trim().replace(/^([-*]|\d+\.)\s+/, '');
        if (/^\s{2,}/.test(raw) && items.length > 0) items[items.length - 1].subs.push(text);
        else items.push({ text, subs: [] });
        i += 1;
      }
      const lis = items.map((it, ii) => (
        <li key={ii}>
          {renderInline(it.text, `li${bk}-${ii}`)}
          {it.subs.length > 0 ? (
            <ul>
              {it.subs.map((sub, si) => (
                <li key={si}>{renderInline(sub, `li${bk}-${ii}-${si}`)}</li>
              ))}
            </ul>
          ) : null}
        </li>
      ));
      out.push(ordered ? <ol key={k++}>{lis}</ol> : <ul key={k++}>{lis}</ul>);
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#|\||[-*]\s|\d+\.\s)/.test(lines[i].trim())) {
      para.push(lines[i].trim());
      i += 1;
    }
    out.push(
      <p key={k++}>
        {para.map((pl, pi) => (
          <React.Fragment key={pi}>
            {pi > 0 ? <br /> : null}
            {renderInline(pl, `p${bk}-${pi}`)}
          </React.Fragment>
        ))}
      </p>
    );
  }
  return out;
}

/** 앱 안에서 넘어왔으면 뒤로, 새 탭·직접 접속이면 홈(/)으로 */
function goBackOrHome() {
  let sameOriginReferrer = false;
  try {
    sameOriginReferrer = !!document.referrer && new URL(document.referrer).origin === window.location.origin;
  } catch {
    sameOriginReferrer = false;
  }
  if (window.history.length > 1 && sameOriginReferrer) {
    window.history.back();
  } else {
    window.location.href = '/';
  }
}

export default function LegalDocumentPage({ kind }: { kind: LegalDocKind }) {
  const theme =
    typeof window !== 'undefined' && window.localStorage.getItem('curriculum-theme') === 'dark' ? 'dark' : 'light';
  const doc = DOCS[kind];
  /** 관리자(/admin/legal) 현행 버전 HTML. null=불러오는 중, ''=없음(기본 원문 사용) */
  const [adminHtml, setAdminHtml] = React.useState<string | null>(null);
  const bodyRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetchSignupLegalDocuments()
      .then((leg) => {
        if (cancelled) return;
        const raw = (kind === 'terms' ? leg.terms_html : leg.privacy_html).trim();
        setAdminHtml(raw ? sanitizeSchoolNoticeHtml(raw) : '');
      })
      .catch(() => {
        if (!cancelled) setAdminHtml('');
      });
    return () => {
      cancelled = true;
    };
  }, [kind]);

  React.useEffect(() => {
    document.title = `${doc.label} | 클래스 플래너`;
  }, [doc.label]);

  React.useEffect(() => {
    if (adminHtml === null) return;
    const root = bodyRef.current;
    if (!root) return;
    if (adminHtml) {
      // 관리자 HTML에는 id가 없으므로 '제4장', '[별표]' 제목을 찾아 앵커 부여
      const els = Array.from(root.querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5,h6,p,div,strong,b,span'));
      const mark = (re: RegExp, id: string) => {
        if (root.querySelector(`#${id}`)) return;
        const hit = els.find((el) => {
          const t = (el.textContent || '').trim();
          return t.length < 80 && re.test(t);
        });
        if (hit) hit.id = id;
      };
      mark(/^제\s*4\s*장/, 'refund');
      mark(/^\[별표\]/, 'refund-detail');
    }
    const hash = window.location.hash.slice(1);
    if (hash) document.getElementById(hash)?.scrollIntoView();
  }, [adminHtml]);

  return (
    <div className={`cp-legal-page cp-legal-page--${theme}`}>
      <header className="cp-legal-header">
        <div className="cp-legal-header-left">
          <button type="button" className="cp-legal-back-btn" onClick={goBackOrHome}>
            <span aria-hidden>←</span> 되돌아가기
          </button>
          <a className="cp-legal-brand" href="/">
            클래스 플래너
          </a>
        </div>
        <nav className="cp-legal-nav" aria-label="법적 고지">
          {(Object.keys(DOCS) as LegalDocKind[]).map((key) => (
            <a
              key={key}
              href={LEGAL_DOC_PATHS[key]}
              className={key === kind ? 'cp-legal-nav-link is-active' : 'cp-legal-nav-link'}
              aria-current={key === kind ? 'page' : undefined}
            >
              {DOCS[key].label}
            </a>
          ))}
        </nav>
      </header>
      {adminHtml === null ? (
        <main className="cp-legal-body" ref={bodyRef} aria-busy="true">
          <p className="cp-legal-loading">불러오는 중…</p>
        </main>
      ) : adminHtml ? (
        <main
          className="cp-legal-body cp-legal-body--html"
          ref={bodyRef}
          dangerouslySetInnerHTML={{ __html: adminHtml }}
        />
      ) : (
        <main className="cp-legal-body" ref={bodyRef}>
          {renderMarkdown(doc.md)}
        </main>
      )}
    </div>
  );
}
